import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card } from '../../components/common/Card';
import { GameSettingsForm } from '../../components/games/GameSettingsForm';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { gamesApi } from '../../api/games.api';
import { UpdateGameSettingsRequest } from '../../types/game.types';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../components/common/Button';

export const EditGame: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();

  const { data: gameData, isLoading } = useQuery({
    queryKey: ['game', gameId],
    queryFn: () => gamesApi.getGame(gameId!),
    enabled: !!gameId,
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (data: UpdateGameSettingsRequest) =>
      gamesApi.updateGameSettings(gameId!, data),
    onSuccess: () => {
      toast.success('Game settings updated successfully');
      navigate('/games');
    },
    onError: () => {
      toast.error('Failed to update game settings');
    },
  });

  if (isLoading) {
    return <LoadingSpinner message="Loading game..." />;
  }

  if (!gameData?.data) {
    return <div>Game not found</div>;
  }

  const game = gameData.data;

  return (
    <div>
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/games')}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          Back to Games
        </Button>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{game.name}</h1>
        <p className="text-gray-600 mt-1">Bundle ID: {game.bundleId}</p>
      </div>

      <Card title="Game Settings">
        <GameSettingsForm
          settings={game.settings}
          adjustConfig={game.adjustConfig}
          onSubmit={async (settings, adjustConfig) => {
            await updateSettingsMutation.mutateAsync({
              settings,
              adjustConfig,
              adjustAppToken: settings.adjustAppToken,
            });
          }}
          isLoading={updateSettingsMutation.isPending}
        />
      </Card>
    </div>
  );
};